import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

import { ClipboardService, CopyKind } from '../../services/clipboard.service';

/** Jak długo ptaszek zostaje na ekranie — wartość ze specyfikacji mikrointerakcji. */
const CONFIRMATION_MS = 1400;

/**
 * Jedyna afordancja kopiowania w aplikacji — ikona do kliknięcia obok wartości
 * (IMEI, e-mail, link do lokalizacji). Wygląd zmienia się TYLKO tutaj i w
 * <app-copy-icon>; komponenty-rodzice nie mają prawa nadpisywać tych stylów.
 *
 * Pozycjonowanie należy do rodzica: na każdym ekranie ikona siada gdzie indziej,
 * więc `position`, `margin` i `top` zostają w SCSS ekranu, pod własną klasą.
 *
 * Zwykłe użycie — rodzaj danych dobiera komunikat i podpowiedź:
 *   <app-copy-button [value]="user.imei" copyKind="imei"></app-copy-button>
 *
 * Przypadek jednorazowy — własny komunikat spoza mapy rodzajów:
 *   <app-copy-button [value]="url" [message]="'VOIP.WEBHOOK_COPIED' | translate"></app-copy-button>
 *
 * Świadomie NIE używa wewnątrz [appCopyOnClick], choć logika wygląda tak samo:
 * <button> sam zamienia Enter i spację na zdarzenie click, więc nasłuch klawiatury
 * z dyrektywy skopiowałby wartość dwa razy.
 */
@Component({
  selector: 'app-copy-button',
  template: `
    <button data-event="EVENTS.SHARED.COMPONENTS.COPY-BUTTON.BUTTON.ON-CLICK" *ngIf="copyable"
            type="button"
            class="copy-button"
            [attr.title]="label || null"
            [attr.aria-label]="label || null"
            (click)="onClick($event)">
      <app-copy-icon [size]="size" [confirmed]="confirmed"></app-copy-icon>
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
    }
    /* Reset przycisku — cały wygląd (kolor, przezroczystość, hover) niesie ikona. */
    .copy-button {
      display: inline-flex;
      align-items: center;
      padding: 0;
      margin: 0;
      border: none;
      background: none;
      color: inherit;
      line-height: 0;
      cursor: pointer;
    }
    .copy-button:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 2px;
    }
  `]
})
export class CopyButtonComponent implements OnChanges, OnDestroy {

  /** Wartość trafiająca do schowka. Pusta lub „-" — komponent się nie renderuje. */
  @Input() value: string | number;

  /** Rodzaj danych; dobiera komunikat i podpowiedź z mapy w ClipboardService. */
  @Input() copyKind: CopyKind;

  /** Gotowy komunikat — nadpisuje ten z `copyKind`, dla przypadków spoza mapy. */
  @Input() message: string;

  /** Gotowa podpowiedź — nadpisuje tę z `copyKind`. */
  @Input() tooltip: string;

  @Input() size = 16;

  copyable = false;
  label = '';
  confirmed = false;

  private confirmationTimer: any = null;
  private destroyed = false;

  constructor(private clipboard: ClipboardService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Pytanie „czy jest co kopiować" zadaje serwis, nie szablon — inaczej ta sama
    // reguła żyłaby osobno w każdym *ngIf w aplikacji.
    this.copyable = this.clipboard.isCopyable(this.value);
    this.label = this.tooltip || (this.copyKind ? this.clipboard.tooltipFor(this.copyKind) : '');

    // Wiersze tabel są recyklingowane przez trackBy, więc ten sam komponent bywa
    // przestawiany na inne dane. Ptaszek nie może zostać przy wartości, której
    // nikt nie kopiował.
    if (changes.value && !changes.value.firstChange) {
      this.clearConfirmation();
    }
  }

  onClick(event: Event): void {
    const copying = this.message
      ? this.clipboard.copy(this.value, this.message, event)
      : this.clipboard.copyAs(this.value, this.copyKind, event);

    copying.then(copied => {
      // Ptaszek dopiero po potwierdzonym sukcesie — zapalenie go „na wiarę" byłoby
      // tym samym kłamstwem, co toast sukcesu przy nieudanym execCommand.
      // Warunek na `destroyed`, bo wiersz mógł zniknąć w trakcie: wtedy ngOnDestroy
      // już przeszedł i nie miałby kto posprzątać założonego tu timera.
      if (copied && !this.destroyed) {
        this.confirm();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearConfirmation();
  }

  /** Kolejne kliknięcie odlicza czas od nowa, zamiast urwać potwierdzenie w połowie. */
  private confirm(): void {
    this.confirmed = true;
    clearTimeout(this.confirmationTimer);
    this.confirmationTimer = setTimeout(() => this.confirmed = false, CONFIRMATION_MS);
  }

  private clearConfirmation(): void {
    clearTimeout(this.confirmationTimer);
    this.confirmed = false;
  }
}
